import { useNavigate, useRouterState } from '@tanstack/react-router';
import { Button, ErrorState, LoadingState, VerydiaWordmark } from '@verydia/ui';
import { useEffect, type ReactNode } from 'react';
import { useAuth } from '../lib/auth/index.ts';
import { describeFailure, isStarting, useApiTokenSource, useMe } from '../lib/api.ts';

/** Routes that must render for a signed-out visitor. */
const PUBLIC_PATHS = new Set(['/login', '/auth/callback', '/auth/error', '/access-denied']);

export function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.has(pathname);
}

/**
 * The waking state.
 *
 * Deliberately says what is happening and that it is normal. "Could not load
 * your access" for a cold database is the kind of message that turns a
 * fifteen-second wait into a support ticket.
 */
function StartingUp({
  exhausted = false,
  requestId = null,
  onRetry,
}: {
  exhausted?: boolean;
  requestId?: string | null;
  onRetry?: () => void;
}) {
  return (
    <div
      className="rounded-xl border border-[var(--vc-border)] bg-[var(--vc-surface)] p-8 text-center shadow-sm"
      role="status"
      aria-live="polite"
    >
      <h1 className="text-lg font-semibold text-[var(--vc-text)]">Verydia is starting up</h1>
      <p className="mt-2 text-sm text-[var(--vc-text-subtle)]">
        {exhausted
          ? 'The control plane is taking longer than expected to start.'
          : 'The control plane was idle and is reconnecting. This usually takes a few seconds.'}
      </p>

      {exhausted ? (
        <>
          <Button variant="primary" className="mt-6" onClick={onRetry}>
            Try again
          </Button>
          {requestId ? (
            // Kept out of the way while things are merely slow, and available
            // once there is something to report.
            <details className="mt-5 text-left">
              <summary className="cursor-pointer text-xs text-[var(--vc-text-subtle)]">
                Diagnostic details
              </summary>
              <p className="mt-2 text-xs text-[var(--vc-text-subtle)]">
                Request <span className="font-mono">{requestId}</span>
              </p>
            </details>
          ) : null}
        </>
      ) : (
        /* Decorative. The wrapper above is already the live region, and a status
           inside a status makes a screen reader announce the wait twice. */
        <div className="mt-6 flex justify-center" aria-hidden="true">
          <span className="size-4 animate-spin rounded-full border-2 border-[var(--vc-border-strong)] border-t-[var(--vc-accent)]" />
        </div>
      )}
    </div>
  );
}

/** A centred full-page frame, used for every state that is not the console itself. */
function Curtain({ children }: { children: ReactNode }) {
  return (
    <main className="grid min-h-dvh place-items-center bg-[var(--vc-surface-sunken)] px-6">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <VerydiaWordmark tone="surface" />
        </div>
        {children}
      </div>
    </main>
  );
}

/**
 * Decides whether the console may render at all.
 *
 * The ordering is the whole point. Nothing protected is rendered until the
 * answer is known — not the shell, not the navigation, not a skeleton of the
 * page. A guard that renders the layout while it "checks" has already leaked
 * which organizations exist, and on a slow connection it leaks them for long
 * enough to read (§29).
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  // The SDK asks for a token per request; this is what answers it.
  useApiTokenSource(auth.getApiToken);

  // /v1/me is the authorization decision. The token proves who someone is; only
  // the API can say what they may reach, so the console asks rather than reading
  // it out of the id token.
  const me = useMe(auth.status === 'authenticated');

  const isPublic = isPublicPath(pathname);

  useEffect(() => {
    if (isPublic || auth.status !== 'anonymous') return;
    void navigate({
      to: '/login',
      search: { returnTo: `${window.location.pathname}${window.location.search}` },
      replace: true,
    });
  }, [auth.status, isPublic, navigate]);

  const access = me.data?.data;
  const hasSomewhereToGo =
    access !== undefined && (access.organizations.length > 0 || access.platform_roles.length > 0);

  useEffect(() => {
    if (isPublic || me.isSuccess === false || access === undefined) return;
    if (!hasSomewhereToGo) {
      void navigate({ to: '/access-denied', replace: true });
      return;
    }

    // A platform operator who belongs to no organization yet.
    //
    // Correct and expected — a platform role grants no membership (ADR-014) —
    // but the Overview is built around an organization, so landing there shows
    // an operator an empty page and no way forward. Send them where the work
    // actually is. This changes navigation, never authorization: the API
    // refuses exactly what it refused before.
    if (pathname === '/' && access.organizations.length === 0 && access.platform_roles.length > 0) {
      void navigate({ to: '/platform/organizations', replace: true });
    }
  }, [access, hasSomewhereToGo, isPublic, me.isSuccess, navigate, pathname]);

  if (isPublic) return <>{children}</>;

  if (auth.status === 'resolving') {
    return (
      <Curtain>
        <LoadingState label="Checking your session" />
      </Curtain>
    );
  }

  // Anonymous: the effect above is redirecting. Render the curtain rather than
  // the console, so the redirect is never a flash of somebody else's data.
  if (auth.status === 'anonymous') {
    return (
      <Curtain>
        <LoadingState label="Redirecting to sign-in" />
      </Curtain>
    );
  }

  if (me.isPending) {
    return (
      <Curtain>
        <LoadingState label="Loading your access" />
      </Curtain>
    );
  }

  // While the control plane wakes, the query is still retrying. This is not an
  // error state and must not look like one: the database scales to zero to keep
  // this deployment near free, so the first request after a quiet period
  // legitimately waits (§14).
  // `failureReason`, not `error`: while a query is still retrying, TanStack Query
  // keeps the last failure there and leaves `error` null until the retries are
  // spent. Reading `error` here would mean the starting-up state never appeared
  // during the very window it exists for.
  const inFlightFailure = me.failureReason ?? me.error;

  if (me.isFetching && isStarting(inFlightFailure)) {
    return (
      <Curtain>
        <StartingUp />
      </Curtain>
    );
  }

  if (me.isError) {
    const failure = describeFailure(me.error);

    // The retry budget is spent and it is still starting. Say that, rather than
    // "could not load your access", which blames the user's account for what is
    // a cold database.
    if (failure.kind === 'starting') {
      return (
        <Curtain>
          <StartingUp exhausted requestId={failure.requestId} onRetry={() => void me.refetch()} />
        </Curtain>
      );
    }

    return (
      <Curtain>
        <div className="rounded-xl border border-[var(--vc-border)] bg-[var(--vc-surface)] p-2 shadow-sm">
          <ErrorState
            title="Could not load your access"
            description={failure.message}
            requestId={failure.requestId}
            onRetry={() => void me.refetch()}
          />
        </div>
      </Curtain>
    );
  }

  if (!hasSomewhereToGo) {
    return (
      <Curtain>
        <LoadingState label="Checking your access" />
      </Curtain>
    );
  }

  return <>{children}</>;
}
